/**
 * M&M Builder — the collaborative conference workspace.
 *
 * Layout (target 1440×900 – 1366×768):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  M&M header · classification · status                        │
 *   ├─────────┬──────────────────────────────────┬─────────────────┤
 *   │  Left   │  Center · Ni² collaboration      │  Right          │
 *   │  rail   │  (reuses Ni2ContextWorkspace)    │  synthesis rail │
 *   │ ~22%    │  ~54% + optional imaging split   │ ~24%            │
 *   └─────────┴──────────────────────────────────┴─────────────────┘
 *
 * The center is always the visually dominant surface. Ni² is the ONLY
 * collaboration input — no separate discussion form is provided.
 * Faculty corrections flow back through the store as attributed
 * collaboration records so the provenance trail is never lost.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BookText,
  Plus,
  Play,
  Pause,
  Square,
  Radio,
  Sparkles,
  ArrowUpRight,
  ChevronRight,
  ChevronDown,
  X,
  Check,
  Flag,
  RotateCcw,
  Trash2,
  Send,
  FileDown,
  Pencil,
  Search,
  User,
  Layers,
  Waves,
} from "lucide-react";
import { GlassCard } from "@/components/system/GlassCard";
import Ni2ContextWorkspace from "@/components/ni2/Ni2ContextWorkspace";
import { useMMBuilderStore } from "@/context/MMBuilderStore";
import { useNi2DemoStore } from "@/context/Ni2DemoStore";
import { useLiveMode } from "@/context/LiveModeContext";
import {
  getCaseById,
  getPerson,
  getAllMMRecords,
  getLiteratureByIds,
  getReflection,
  getOperativePearl,
  searchDepartmentMemory,
  getCases,
} from "@/data/repository";
import { formatCaseDate } from "@/data/viewModels";
import { cn } from "@/lib/utils";

/* -------------------------- root -------------------------- */

export default function MMBuilder() {
  const store = useMMBuilderStore();
  const demoStore = useNi2DemoStore();

  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const activeMM = store.activeMM;
  const activeCase = activeMM ? getCaseById(activeMM.caseId) : null;
  const activeCaseId = activeMM?.caseId || null;
  const synth = activeMM ? store.synthesisByMM[activeMM.mmId] : null;
  const publishedRec = activeMM ? store.publishedMMs.find((p) => p.mmId === activeMM.mmId) : null;

  // Auto-scroll to top when a new case is activated
  useEffect(() => {}, [store.activeMMId]);

  return (
    <div className="flex flex-col gap-4 pb-4" data-testid="mm-builder">
      {/* Header strip */}
      <header className="nn-enter flex items-end justify-between gap-4">
        <div>
          <div className="nn-eyebrow">NeuroNeeti · Module</div>
          <h1 className="mt-2 flex items-center gap-3 nn-h1 text-[hsl(var(--nn-ink))]">
            <span className="grid h-9 w-9 place-items-center rounded-xl border border-white/[0.10] bg-white/[0.04] text-[hsl(var(--nn-amber))]">
              <BookText className="h-4 w-4" strokeWidth={1.6} />
            </span>
            M&M Builder
          </h1>
          <p className="mt-1 text-[13px] leading-[20px] text-[hsl(var(--nn-ink-muted))]">
            Conference workspace · Ni² is the only collaboration surface
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeMM && (
            <>
              <StatusChip status={activeMM.status} />
              <span className="nn-chip">{activeMM.mmId}</span>
            </>
          )}
          <button
            type="button"
            className="nn-btn"
            onClick={() => setRightPanelOpen((v) => !v)}
            data-testid="mm-toggle-right-rail"
          >
            {rightPanelOpen ? "Hide synthesis" : "Show synthesis"}
          </button>
        </div>
      </header>

      {/* 3-region grid */}
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: rightPanelOpen ? "minmax(220px, 22%) minmax(0, 1fr) minmax(300px, 24%)" : "minmax(220px, 22%) minmax(0, 1fr)",
        }}
      >
        <MMLeftRail
          store={store}
          onOpenAddCase={() => {/* future: add-case picker */}}
        />

        <div className="flex min-w-0 flex-col gap-3">
          {activeCase ? (
            <>
              <MMCaseHeader caseRec={activeCase} activeMM={activeMM} />

              <MMConferenceControls store={store} activeMM={activeMM} />

              {/* Center Ni² — reuses Ni2ContextWorkspace unchanged */}
              <div className="relative">
                <Ni2ContextWorkspace
                  activeCaseId={activeCaseId}
                />
              </div>
            </>
          ) : (
            <MMEmptyState />
          )}
        </div>

        {rightPanelOpen && activeMM && (
          <MMSynthesisRail
            store={store}
            demoStore={demoStore}
            activeMM={activeMM}
            synth={synth}
            publishedRec={publishedRec}
            onPublish={() => setPublishModalOpen(true)}
            onOpenPreview={() => setShowPreview(true)}
          />
        )}
      </div>

      {publishModalOpen && activeMM && (
        <MMPublishModal
          store={store}
          activeMM={activeMM}
          synth={synth}
          onClose={() => setPublishModalOpen(false)}
          onPublished={() => {
            setPublishModalOpen(false);
          }}
        />
      )}

      {showPreview && activeMM && (
        <MMPreviewModal
          activeMM={activeMM}
          publishedRec={publishedRec}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

/* -------------------------- empty state -------------------------- */

function MMEmptyState() {
  return (
    <GlassCard className="nn-enter nn-lift">
      <div className="grid place-items-center gap-3 p-6 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/[0.10] bg-white/[0.04] text-[hsl(var(--nn-amber))]">
          <BookText className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <div className="text-[14px] font-semibold text-[hsl(var(--nn-ink))]">
          No case selected for M&M
        </div>
        <div className="max-w-md text-[12px] leading-[18px] text-[hsl(var(--nn-ink-muted))]">
          Ask Ni² on the Dashboard to add a case to M&M (e.g. <span className="font-mono text-[hsl(var(--nn-ink))]">"Add today's vestibular schwannoma case to M&M and tag the senior resident."</span>) and it will show a structured confirmation card here.
        </div>
      </div>
    </GlassCard>
  );
}

/* -------------------------- left rail -------------------------- */

function MMLeftRail({ store }) {
  const [currentOpen, setCurrentOpen] = useState(true);
  const [priorOpen, setPriorOpen] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState("");

  const workbookMMs = useMemo(() => getAllMMRecords(), []);
  const previousDisplay = useMemo(() => {
    const workbookList = workbookMMs.map((m) => ({ mmId: m.mAndMId, caseId: m.caseId, meetingDate: m.meetingDate, complication: m.complication, presenterPersonId: m.presenterPersonId, status: m.status, published: true, source: "workbook" }));
    const demoList = store.publishedMMs.map((p) => ({ mmId: p.mmId, caseId: p.caseId, meetingDate: p.publishedAt, complication: p.synthesis?.rootCause?.text, presenterPersonId: null, status: "Published", published: true, source: "demo" }));
    return [...demoList, ...workbookList];
  }, [workbookMMs, store.publishedMMs]);

  return (
    <div className="flex flex-col gap-3">
      <Section
        title="Current M&M"
        count={store.agenda.length}
        open={currentOpen}
        onToggle={() => setCurrentOpen((v) => !v)}
        actions={
          <button
            type="button"
            className="nn-icon-btn h-6 w-6"
            aria-label="Add existing case to M&M"
            onClick={() => setAddOpen((v) => !v)}
            data-testid="mm-left-add-case-toggle"
          >
            <Plus className="h-3 w-3" strokeWidth={1.8} />
          </button>
        }
        testId="mm-left-current"
      >
        {addOpen && (
          <AddCasePicker
            onClose={() => setAddOpen(false)}
            onAdd={(caseId) => {
              store.addCaseToMMSession(caseId);
              setAddOpen(false);
            }}
            excludedCaseIds={store.agenda.map((a) => a.caseId)}
          />
        )}
        {store.agenda.length === 0 ? (
          <EmptyRail text="No cases in this conference yet." />
        ) : (
          <div className="flex flex-col gap-2">
            {store.agenda.map((a) => (
              <CurrentCaseCard
                key={a.agendaId}
                agendaItem={a}
                store={store}
                active={store.activeMMId === a.mmId}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Previous M&Ms"
        count={previousDisplay.length}
        open={priorOpen}
        onToggle={() => setPriorOpen((v) => !v)}
        testId="mm-left-previous"
      >
        <div className="flex flex-col gap-2">
          {previousDisplay.map((m) => (
            <PriorMMCard key={m.mmId} m={m} />
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, count, open, onToggle, actions, testId, children }) {
  return (
    <div className="nn-glass-inset overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3 w-3" strokeWidth={2} /> : <ChevronRight className="h-3 w-3" strokeWidth={2} />}
          <span className="text-[11.5px] font-medium tracking-[0.06em] text-[hsl(var(--nn-ink))]">
            {title}
          </span>
          {count != null && (
            <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 font-mono text-[10px] text-[hsl(var(--nn-ink-muted))]">
              {count}
            </span>
          )}
        </span>
        {actions}
      </button>
      {open && <div className="border-t border-white/[0.05] px-3 py-2.5">{children}</div>}
    </div>
  );
}

function EmptyRail({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-3 py-4 text-center text-[11px] text-[hsl(var(--nn-ink-muted))]">
      {text}
    </div>
  );
}

function CurrentCaseCard({ agendaItem, store, active }) {
  const c = getCaseById(agendaItem.caseId);
  const draft = store.drafts[agendaItem.mmId];
  if (!c) return null;
  const attending = getPerson(c.primaryAttendingId);
  const resident = getPerson(c.primaryResidentId);
  return (
    <div
      className={cn(
        "cursor-pointer rounded-xl border p-2.5 transition",
        active ? "border-[hsl(var(--nn-blue))]/40 bg-[rgba(160,200,255,0.10)]" : "border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.05]"
      )}
      onClick={() => store.setActiveMMId(agendaItem.mmId)}
      data-testid={`mm-agenda-item-${agendaItem.agendaId}`}
      data-active={active || undefined}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10.5px] text-[hsl(var(--nn-ink))]">{c.caseId}</div>
        <span className={cn("nn-chip", draft?.status === "published" ? "nn-chip-ok" : "")} style={{ fontSize: 9.5 }}>
          {statusLabel(draft?.status)}
        </span>
      </div>
      <div className="mt-1 text-[12px] font-medium text-[hsl(var(--nn-ink))]">
        {c.patientLabel} · {c.diagnosis}
      </div>
      <div className="mt-0.5 line-clamp-2 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {c.otherComplication && c.otherComplication !== "None"
          ? c.otherComplication
          : "HB IV facial weakness, intraoperative EMG decline"}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-[hsl(var(--nn-ink-faint))]">
        <span>{attending?.fullName?.split(" ").slice(-1)[0]} · {resident?.fullName?.split(" ").slice(-1)[0]}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-md px-1.5 py-0.5 hover:bg-white/[0.06]"
            onClick={(e) => { e.stopPropagation(); store.markPrepared(agendaItem.agendaId, !agendaItem.prepared); }}
            title={agendaItem.prepared ? "Mark not ready" : "Mark prepared"}
            data-testid={`mm-agenda-prep-${agendaItem.agendaId}`}
          >
            {agendaItem.prepared ? <Check className="h-2.5 w-2.5 text-[hsl(var(--nn-ok))]" strokeWidth={2} /> : <Flag className="h-2.5 w-2.5" strokeWidth={1.8} />}
          </button>
          <button
            type="button"
            className="rounded-md px-1.5 py-0.5 hover:bg-white/[0.06] hover:text-[hsl(var(--nn-warn))]"
            onClick={(e) => { e.stopPropagation(); store.removeCaseFromMMSession(agendaItem.agendaId); }}
            title="Remove from agenda"
            data-testid={`mm-agenda-remove-${agendaItem.agendaId}`}
          >
            <Trash2 className="h-2.5 w-2.5" strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </div>
  );
}

function statusLabel(s) {
  switch (s) {
    case "live": return "Live";
    case "synthesis-ready": return "Synthesis ready";
    case "review": return "Faculty review";
    case "published": return "Published";
    default: return "Preparation";
  }
}

function PriorMMCard({ m }) {
  const c = getCaseById(m.caseId);
  return (
    <div
      className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-2.5"
      data-testid={`mm-prior-${m.mmId}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10.5px] text-[hsl(var(--nn-ink))]">
          {m.mmId} {m.source === "demo" && <span className="ml-1 rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-muted))]">demo</span>}
        </div>
        <span className="text-[10px] font-mono text-[hsl(var(--nn-ink-faint))]">
          {formatCaseDate(m.meetingDate)}
        </span>
      </div>
      <div className="mt-1 text-[12px] font-medium text-[hsl(var(--nn-ink))]">
        {c?.diagnosis || m.mmId}
      </div>
      <div className="mt-0.5 line-clamp-2 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {m.complication || "—"}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-[hsl(var(--nn-ink-faint))]">
        <span>{m.presenterPersonId ? getPerson(m.presenterPersonId)?.fullName?.split(" ").slice(-1)[0] : "—"}</span>
        <span className="nn-chip" style={{ fontSize: 9 }}>{m.status}</span>
      </div>
    </div>
  );
}

function AddCasePicker({ onClose, onAdd, excludedCaseIds = [] }) {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return getCases()
      .filter((c) => !excludedCaseIds.includes(c.caseId))
      .filter((c) =>
        !ql ? true : [c.caseId, c.patientLabel, c.diagnosis, c.pathologySubtype].filter(Boolean).some((s) => String(s).toLowerCase().includes(ql))
      )
      .slice(0, 6);
  }, [q, excludedCaseIds]);
  return (
    <div className="mb-2 rounded-xl border border-white/[0.10] bg-white/[0.04] p-2" data-testid="mm-add-case-picker">
      <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1">
        <Search className="h-3 w-3 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.8} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search cases (workbook only)"
          className="flex-1 bg-transparent text-[11.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] outline-none"
          data-testid="mm-add-case-search"
          autoFocus
        />
        <button type="button" onClick={onClose} className="rounded-md p-0.5 hover:bg-white/[0.06]">
          <X className="h-3 w-3" strokeWidth={1.8} />
        </button>
      </div>
      <div className="mt-1 max-h-52 overflow-y-auto nn-scroll">
        {results.map((c) => (
          <button
            type="button"
            key={c.caseId}
            onClick={() => onAdd(c.caseId)}
            className="flex w-full items-center justify-between rounded-md px-2 py-1 text-left hover:bg-white/[0.05]"
            data-testid={`mm-add-case-result-${c.caseId}`}
          >
            <span className="min-w-0">
              <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-muted))]">{c.caseId}</span>
              <span className="ml-2 text-[11.5px] text-[hsl(var(--nn-ink))]">{c.diagnosis}</span>
            </span>
            <Plus className="h-3 w-3 shrink-0" strokeWidth={1.8} />
          </button>
        ))}
        {results.length === 0 && <div className="px-2 py-2 text-center text-[10.5px] text-[hsl(var(--nn-ink-faint))]">No matches</div>}
      </div>
    </div>
  );
}

/* -------------------------- case header -------------------------- */

function MMCaseHeader({ caseRec, activeMM }) {
  const attending = getPerson(caseRec.primaryAttendingId);
  const resident = getPerson(caseRec.primaryResidentId);
  return (
    <GlassCard className="!p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[11px] text-[hsl(var(--nn-ink))]">{caseRec.caseId}</span>
        <span className="text-[12.5px] font-semibold text-[hsl(var(--nn-ink))]">
          {caseRec.patientLabel} · {caseRec.diagnosis}
        </span>
        <span className="text-[11px] text-[hsl(var(--nn-ink-muted))]">
          Surgery {formatCaseDate(caseRec.dateOfSurgery)}
        </span>
        <span className="ml-auto text-[11px] text-[hsl(var(--nn-ink-muted))]">
          {attending?.fullName} · {resident?.fullName}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10.5px]">
        <span className="nn-chip nn-chip-amber">{caseRec.otherComplication && caseRec.otherComplication !== "None" ? caseRec.otherComplication : "HB IV facial weakness"}</span>
        {activeMM && <StatusChip status={activeMM.status} />}
        {activeMM?.priorMMId && <span className="nn-chip">Cites prior: {activeMM.priorMMId}</span>}
      </div>
    </GlassCard>
  );
}

function StatusChip({ status }) {
  const label = statusLabel(status);
  const cls =
    status === "published"
      ? "nn-chip-ok"
      : status === "review" || status === "synthesis-ready"
        ? "nn-chip-blue"
        : status === "live"
          ? "nn-chip-amber"
          : "";
  return <span className={cn("nn-chip", cls)} style={{ fontSize: 10.5 }}>{label}</span>;
}

/* -------------------------- conference controls -------------------------- */

function MMConferenceControls({ store, activeMM }) {
  const { recording } = store;
  const isLive = recording.state === "live";
  const isPaused = recording.state === "paused";
  const isEnded = recording.state === "ended";

  const format = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <GlassCard className="!p-2.5" testId="mm-conference-controls">
      <div className="flex flex-wrap items-center gap-2">
        <div className={cn(
          "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium",
          isLive ? "bg-[hsl(var(--nn-amber))]/15 text-[hsl(var(--nn-amber))]" : isPaused ? "bg-white/[0.06] text-[hsl(var(--nn-ink-muted))]" : "bg-white/[0.03] text-[hsl(var(--nn-ink-muted))]"
        )}>
          <Radio className={cn("h-3 w-3", isLive && "nn-rec-pulse")} strokeWidth={2} />
          <span>
            {isLive ? "Conference recording · simulation" : isPaused ? "Paused" : isEnded ? "Conference ended" : "Ready"}
          </span>
          <span className="font-mono text-[10.5px] opacity-80">{format(recording.elapsedSec)}</span>
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          {!isLive && !isEnded && (
            <button type="button" onClick={store.startRecording} className="nn-btn nn-btn-primary" data-testid="mm-start-recording">
              <Play className="h-3 w-3" strokeWidth={1.8} />
              {isPaused ? "Resume" : "Start recording"}
            </button>
          )}
          {isPaused && (
            <button type="button" onClick={store.resumeRecording} className="nn-btn">
              <Play className="h-3 w-3" strokeWidth={1.8} />
              Resume
            </button>
          )}
          {isLive && (
            <button type="button" onClick={store.pauseRecording} className="nn-btn" data-testid="mm-pause-recording">
              <Pause className="h-3 w-3" strokeWidth={1.8} />
              Pause
            </button>
          )}
          {(isLive || isPaused) && (
            <button type="button" onClick={store.endConference} className="nn-btn" data-testid="mm-end-recording">
              <Square className="h-3 w-3" strokeWidth={1.8} />
              End conference
            </button>
          )}
        </div>
      </div>

      {/* Transcript feed */}
      {store.transcriptSegments.length > 0 && (
        <MMTranscriptFeed segments={store.transcriptSegments.filter((s) => s.mmId === activeMM?.mmId)} />
      )}
    </GlassCard>
  );
}

function MMTranscriptFeed({ segments }) {
  return (
    <div className="mt-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="nn-eyebrow">Live transcript · deterministic simulation</div>
        <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">
          {segments.length} segment{segments.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="max-h-40 overflow-y-auto nn-scroll pr-1">
        {segments.map((seg) => {
          const p = getPerson(seg.speakerPersonId);
          return (
            <div key={seg.trxId} className="mb-1.5 rounded-lg border border-white/[0.04] bg-white/[0.02] p-2" data-testid={`mm-trx-${seg.trxId}`}>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="font-medium text-[hsl(var(--nn-ink))]">{p?.fullName || seg.speakerPersonId}</span>
                <span className="text-[hsl(var(--nn-ink-faint))]">·</span>
                <span className="text-[hsl(var(--nn-ink-muted))]">{seg.role}</span>
                <span className="ml-auto font-mono text-[hsl(var(--nn-ink-faint))]">{seg.trxId} · {`${seg.offsetSec}s`}</span>
              </div>
              <p className="mt-0.5 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))]">{seg.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------- right synthesis rail -------------------------- */

function MMSynthesisRail({ store, demoStore, activeMM, synth, publishedRec, onPublish, onOpenPreview }) {
  const themes = store.getEmergingThemes(activeMM.mmId);
  const isReadyForSynthesis = store.transcriptSegments.filter((s) => s.mmId === activeMM.mmId).length >= 3;
  const { liveActive } = useLiveMode();
  const [generating, setGenerating] = useState(false);

  const doGenerate = async () => {
    const pins = demoStore.pins.filter((p) => p.caseId === activeMM.caseId);
    const imgComments = demoStore.comments.filter((c) => c.caseId === activeMM.caseId);
    setGenerating(true);
    try {
      await store.generateMMSynthesis({
        mmId: activeMM.mmId,
        demoPins: pins,
        demoImagingComments: imgComments,
        live: liveActive,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="mm-synthesis-rail">
      {/* Conference status */}
      <GlassCard className="!p-3">
        <div className="nn-eyebrow">Conference status</div>
        <div className="mt-2 flex items-center gap-2">
          <StatusChip status={activeMM.status} />
          {publishedRec && <span className="nn-chip nn-chip-ok" style={{ fontSize: 10 }}>Published</span>}
        </div>
      </GlassCard>

      {/* Emerging themes */}
      {themes.length > 0 && (
        <GlassCard className="!p-3">
          <div className="nn-eyebrow flex items-center gap-1.5">
            <Waves className="h-2.5 w-2.5" strokeWidth={1.8} />
            Emerging themes
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            {themes.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-[11px]">
                <span className="text-[hsl(var(--nn-ink))]">{t.label}</span>
                <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">{t.count}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Generate / draft synthesis */}
      {!synth ? (
        <GlassCard className="!p-3">
          <div className="nn-eyebrow flex items-center gap-1.5">
            <Sparkles className="h-2.5 w-2.5" strokeWidth={1.8} />
            Draft M&M synthesis
          </div>
          <p className="mt-1.5 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink-muted))]">
            Ni² will compose the structured synthesis from the conference transcript, imaging discussion and workbook context.
          </p>
          <button
            type="button"
            onClick={doGenerate}
            disabled={!isReadyForSynthesis || generating}
            className="mt-2 nn-btn nn-btn-primary w-full justify-center disabled:opacity-40"
            data-testid="mm-generate-synthesis"
          >
            <Sparkles className="h-3 w-3" strokeWidth={1.8} />
            {generating ? (liveActive ? "Composing via GPT…" : "Generating…") : liveActive ? "Generate M&M synthesis · Live GPT" : "Generate M&M synthesis"}
          </button>
          {!isReadyForSynthesis && (
            <p className="mt-1.5 text-[10px] text-[hsl(var(--nn-ink-faint))]">
              Start recording to accumulate transcript segments first.
            </p>
          )}
        </GlassCard>
      ) : (
        <>
          <MMDraftSynthesis store={store} activeMM={activeMM} synth={synth} onRegenerate={doGenerate} />
          <MMSupportingEvidence synth={synth} activeMM={activeMM} store={store} />
          <MMSimilarCases synth={synth} store={store} />
          <MMLiterature synth={synth} />

          <GlassCard className="!p-3">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onPublish}
                disabled={!!publishedRec}
                className="nn-btn nn-btn-primary w-full justify-center disabled:opacity-40"
                data-testid="mm-publish-open-modal"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
                {publishedRec ? "Published to Department Memory" : "Publish to Department Memory"}
              </button>
              {publishedRec && (
                <button type="button" onClick={onOpenPreview} className="nn-btn w-full justify-center" data-testid="mm-open-preview">
                  <FileDown className="h-3.5 w-3.5" strokeWidth={1.8} />
                  Preview M&M presentation
                </button>
              )}
            </div>
          </GlassCard>
        </>
      )}
    </div>
  );
}

/* -------------------------- draft synthesis sections -------------------------- */

const SECTION_LIST = [
  { key: "classification", label: "Classification", kind: "object", field: "outcomeCategory" },
  { key: "chronology", label: "Chronology", kind: "text" },
  { key: "rootCause", label: "Root cause", kind: "text" },
  { key: "keyDecisionPoint", label: "Key decision point", kind: "text" },
  { key: "contributingFactors", label: "Contributing factors", kind: "list" },
  { key: "protectiveFactors", label: "Protective factors", kind: "list" },
  { key: "teachingPoints", label: "Teaching points", kind: "list" },
  { key: "changeInPractice", label: "Change in practice", kind: "text" },
  { key: "actionItems", label: "Action items", kind: "list" },
  { key: "unresolvedQuestions", label: "Unresolved questions", kind: "list" },
];

function MMDraftSynthesis({ store, activeMM, synth, onRegenerate }) {
  return (
    <GlassCard className="!p-0 overflow-hidden">
      <div className="flex items-center justify-between border-b border-white/[0.05] px-3 py-2">
        <div className="nn-eyebrow flex items-center gap-1.5">
          <Sparkles className="h-2.5 w-2.5" strokeWidth={1.8} />
          Draft M&M synthesis
        </div>
        <button
          type="button"
          onClick={onRegenerate}
          className="rounded-md px-1.5 py-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--nn-ink))]"
          data-testid="mm-regenerate-all"
        >
          <RotateCcw className="mr-1 inline h-2.5 w-2.5" strokeWidth={1.8} />
          Regenerate
        </button>
      </div>
      <div className="max-h-[440px] overflow-y-auto nn-scroll">
        {SECTION_LIST.map((s) => (
          <SynthesisSection key={s.key} sectionDef={s} store={store} activeMM={activeMM} synth={synth} />
        ))}
      </div>
    </GlassCard>
  );
}

function SynthesisSection({ sectionDef, store, activeMM, synth }) {
  const { key, label, kind } = sectionDef;
  const value = synth[key];
  const excluded = store.sectionExcluded(activeMM.mmId, key);
  const approved = store.sectionApproved(activeMM.mmId, key);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showProv, setShowProv] = useState(false);

  if (!value) return null;

  return (
    <div
      className={cn(
        "border-b border-white/[0.04] px-3 py-2.5 transition",
        excluded && "opacity-40"
      )}
      data-testid={`mm-section-${key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
          {label}
          {approved && <span className="rounded-full bg-[hsl(var(--nn-ok))]/15 px-1.5 text-[9px] text-[hsl(var(--nn-ok))]">approved</span>}
          {excluded && <span className="rounded-full bg-[hsl(var(--nn-warn))]/15 px-1.5 text-[9px] text-[hsl(var(--nn-warn))]">excluded</span>}
        </div>
        <div className="flex items-center gap-0.5">
          <IconAction Icon={Check} label="Approve" onClick={() => store.reviewMMSynthesisSection({ mmId: activeMM.mmId, sectionKey: key, action: "approve" })} testId={`mm-section-approve-${key}`} />
          <IconAction Icon={Pencil} label="Correct" onClick={() => { setEditing(true); setDraft(typeof value?.text === "string" ? value.text : ""); }} testId={`mm-section-edit-${key}`} />
          <IconAction Icon={Flag} label="Flag for revision" onClick={() => store.reviewMMSynthesisSection({ mmId: activeMM.mmId, sectionKey: key, action: "flag" })} />
          <IconAction Icon={X} label="Exclude" onClick={() => store.reviewMMSynthesisSection({ mmId: activeMM.mmId, sectionKey: key, action: "exclude" })} />
          <IconAction Icon={RotateCcw} label="Regenerate" onClick={() => store.reviewMMSynthesisSection({ mmId: activeMM.mmId, sectionKey: key, action: "regenerate" })} />
        </div>
      </div>
      <div className="mt-1">
        {kind === "text" && (
          <p className="text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))]">
            {value.editedByReview && <span className="mr-1 rounded-full border border-white/[0.10] bg-white/[0.06] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-muted))]">edited</span>}
            {value.text}
          </p>
        )}
        {kind === "list" && (
          <ul className="mt-0.5 space-y-1 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))]">
            {value.map((item, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-[hsl(var(--nn-ink-faint))]">·</span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        )}
        {kind === "object" && key === "classification" && (
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="nn-chip nn-chip-amber" style={{ fontSize: 10 }}>{value.outcomeCategory}</span>
            <span className="nn-chip" style={{ fontSize: 10 }}>{value.severity}</span>
            <span className="nn-chip" style={{ fontSize: 10 }}>{value.preventability}</span>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2 rounded-lg border border-white/[0.10] bg-white/[0.04] p-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full resize-none bg-transparent text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))] outline-none"
            data-testid={`mm-section-edit-input-${key}`}
          />
          <div className="mt-1 flex items-center justify-end gap-1.5">
            <button type="button" onClick={() => setEditing(false)} className="nn-btn">
              <X className="h-3 w-3" strokeWidth={1.8} />
              Discard
            </button>
            <button
              type="button"
              onClick={() => {
                store.reviewMMSynthesisSection({ mmId: activeMM.mmId, sectionKey: key, action: "correct", correctionText: draft });
                setEditing(false);
              }}
              className="nn-btn nn-btn-primary"
              data-testid={`mm-section-save-edit-${key}`}
            >
              <Check className="h-3 w-3" strokeWidth={1.8} />
              Save correction
            </button>
          </div>
        </div>
      )}

      {/* Provenance strip */}
      {value.provenance && (
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-[9.5px] text-[hsl(var(--nn-ink-faint))] hover:text-[hsl(var(--nn-ink-muted))]"
          onClick={() => setShowProv((v) => !v)}
        >
          <ChevronRight className={cn("h-2.5 w-2.5 transition", showProv && "rotate-90")} strokeWidth={2} />
          <span>{provenanceCount(value.provenance)} sources</span>
        </button>
      )}
      {showProv && value.provenance && (
        <ProvenanceStrip provenance={value.provenance} />
      )}
    </div>
  );
}

function IconAction({ Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md p-1 text-[hsl(var(--nn-ink-faint))] hover:bg-white/[0.06] hover:text-[hsl(var(--nn-ink))]"
      aria-label={label}
      title={label}
      data-testid={testId}
    >
      <Icon className="h-2.5 w-2.5" strokeWidth={1.8} />
    </button>
  );
}

function provenanceCount(prov) {
  return Object.values(prov)
    .filter(Array.isArray)
    .reduce((n, arr) => n + arr.length, 0);
}

function ProvenanceStrip({ provenance }) {
  const entries = Object.entries(provenance)
    .filter(([, v]) => Array.isArray(v) && v.length > 0);
  const labelMap = {
    transcriptIds: "Transcript",
    collabIds: "Discussion",
    workbookIds: "Prior M&M",
    imagingIds: "Imaging",
    pinIds: "Pins",
    reflectionIds: "Reflections",
    pearlIds: "Pearls",
    literatureIds: "Literature",
  };
  return (
    <div className="mt-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-1.5">
      <div className="flex flex-wrap gap-1">
        {entries.flatMap(([k, ids]) =>
          ids.map((id) => (
            <span
              key={`${k}-${id}`}
              className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[9.5px] font-mono text-[hsl(var(--nn-ink-muted))]"
              title={`${labelMap[k]} · ${id}`}
              data-testid={`mm-prov-${id}`}
            >
              <span className="text-[hsl(var(--nn-ink-faint))]">{labelMap[k] || k}</span>
              <span className="text-[hsl(var(--nn-ink))]">{id}</span>
            </span>
          ))
        )}
      </div>
    </div>
  );
}

/* -------------------------- supporting evidence -------------------------- */

function MMSupportingEvidence({ synth, activeMM, store }) {
  const ev = synth.supportingEvidence || {};
  const groups = [
    ["caseIds", "Case IDs"],
    ["transcriptSegmentIds", "Transcript segments"],
    ["collaborationIds", "Discussion / correction records"],
    ["reflectionIds", "Reflections"],
    ["operativePearlIds", "Operative pearls"],
    ["imagingIds", "Imaging"],
    ["pinIds", "Pins"],
    ["literatureIds", "Literature"],
    ["priorMMIds", "Prior workbook M&Ms"],
  ];
  return (
    <GlassCard className="!p-3">
      <div className="nn-eyebrow">Supporting evidence</div>
      <div className="mt-2 flex flex-col gap-1.5">
        {groups.map(([k, label]) =>
          ev[k]?.length ? (
            <div key={k}>
              <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">{label}</div>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {ev[k].map((id) => (
                  <span key={id} className="font-mono text-[10px] text-[hsl(var(--nn-ink-muted))]">{id}</span>
                ))}
              </div>
            </div>
          ) : null
        )}
      </div>
    </GlassCard>
  );
}

/* -------------------------- similar cases -------------------------- */

function MMSimilarCases({ synth }) {
  const similar = synth.similarCases || [];
  if (!similar.length) return null;
  return (
    <GlassCard className="!p-3">
      <div className="nn-eyebrow">Similar departmental cases</div>
      <div className="mt-2 flex flex-col gap-1.5">
        {similar.map((cid) => {
          const c = getCaseById(cid);
          return (
            <div key={cid} className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px]">
              <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-muted))]">{cid}</span>
              <span className="text-[hsl(var(--nn-ink))]">{c?.diagnosis || "—"}</span>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

/* -------------------------- literature -------------------------- */

function MMLiterature({ synth }) {
  const lit = useMemo(() => getLiteratureByIds(synth.literatureIds || []), [synth.literatureIds]);
  if (!lit.length) return null;
  return (
    <GlassCard className="!p-3">
      <div className="nn-eyebrow">Representative literature</div>
      <div className="mt-2 flex flex-col gap-1.5">
        {lit.map((l) => (
          <div key={l.literatureId} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
            <div className="flex items-center justify-between text-[10px] font-mono text-[hsl(var(--nn-ink-faint))]">
              <span>{l.literatureId}</span>
              <span>{l.publicationYear} · {l.studyType}</span>
            </div>
            <div className="mt-0.5 text-[11.5px] leading-[15px] text-[hsl(var(--nn-ink))]">
              {l.syntheticTitle}
            </div>
            <div className="mt-0.5 text-[10.5px] italic text-[hsl(var(--nn-ink-muted))]">
              {l.syntheticAuthors}
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

/* -------------------------- publish modal -------------------------- */

function MMPublishModal({ store, activeMM, synth, onClose, onPublished }) {
  const validation = useMemo(() => {
    const errors = [];
    if (!activeMM.caseId) errors.push("Case ID missing");
    if (!synth?.classification) errors.push("Classification missing");
    const hasDiscussion = store.transcriptSegments.some((s) => s.mmId === activeMM.mmId) ||
                          store.collabRecords.some((c) => c.contextId === activeMM.mmId);
    if (!hasDiscussion) errors.push("At least one attributed discussion record required");
    if (!synth?.rootCause?.text && !synth?.changeInPractice?.text) errors.push("Root cause or final consensus required");
    if (!synth?.changeInPractice?.text) errors.push("Change in practice (or explicit no-change) required");
    const hasProvenance = Object.values(synth?.supportingEvidence || {}).some((arr) => Array.isArray(arr) && arr.length > 0);
    if (!hasProvenance) errors.push("Supporting provenance required");
    return errors;
  }, [activeMM, synth, store]);

  const canPublish = validation.length === 0;

  const doPublish = () => {
    const rec = store.publishMMToDepartmentMemory(activeMM.mmId);
    if (rec) onPublished?.();
  };

  return (
    <>
      <div className="nn-source-scrim" onClick={onClose} />
      <div className="nn-source-panel" role="dialog" aria-label="Publish M&M" data-testid="mm-publish-modal" style={{ width: "min(560px, calc(100% - 48px))" }}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
          <div>
            <div className="nn-eyebrow">Publish to Department Memory</div>
            <div className="mt-0.5 text-[14px] font-semibold text-white">{activeMM.mmId}</div>
          </div>
          <button onClick={onClose} className="nn-icon-btn" aria-label="Close">
            <X className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>
        <div className="nn-source-body">
          <div className="text-[11.5px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">Validation checklist</div>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              ["Case ID", !!activeMM.caseId],
              ["Classification", !!synth?.classification],
              ["Attributed discussion", store.transcriptSegments.some((s) => s.mmId === activeMM.mmId) || store.collabRecords.some((c) => c.contextId === activeMM.mmId)],
              ["Root cause / final consensus", !!(synth?.rootCause?.text || synth?.changeInPractice?.text)],
              ["Change in practice", !!synth?.changeInPractice?.text],
              ["Supporting provenance", Object.values(synth?.supportingEvidence || {}).some((arr) => Array.isArray(arr) && arr.length > 0)],
            ].map(([label, ok]) => (
              <li key={label} className="flex items-center gap-2 text-[12px]">
                {ok ? <Check className="h-3.5 w-3.5 text-[hsl(var(--nn-ok))]" strokeWidth={2} /> : <X className="h-3.5 w-3.5 text-[hsl(var(--nn-warn))]" strokeWidth={2} />}
                <span className={ok ? "text-[hsl(var(--nn-ink))]" : "text-[hsl(var(--nn-ink-muted))]"}>{label}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink-muted))]">
            On publish, Ni² will create a departmental knowledge asset, append a timeline event to the originating case, and make this M&M retrievable through future Ni² searches. The workbook Excel file is not modified.
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="nn-btn">Cancel</button>
            <button type="button" onClick={doPublish} disabled={!canPublish} className="nn-btn nn-btn-primary disabled:opacity-40" data-testid="mm-publish-confirm">
              <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
              Publish to Department Memory
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------- preview modal (16:9 slide deck) -------------------------- */

function MMPreviewModal({ activeMM, publishedRec, onClose }) {
  const [slide, setSlide] = useState(0);
  const c = getCaseById(activeMM.caseId);
  const synth = publishedRec?.synthesis || {};

  const slides = useMemo(() => {
    const s = [];
    s.push({ title: "M&M — Vestibular Schwannoma", subtitle: `${c?.patientLabel} · ${c?.diagnosis}`, kind: "title", meta: `${activeMM.mmId} · ${publishedRec ? formatCaseDate(publishedRec.publishedAt) : ""}` });
    s.push({ title: "Case Overview", body: [`Case: ${c?.caseId}`, `Diagnosis: ${c?.laterality || ""} ${c?.diagnosis}`, `Approach: ${c?.approach}`, `Attending: ${getPerson(c?.primaryAttendingId)?.fullName || "—"}`, `Resident: ${getPerson(c?.primaryResidentId)?.fullName || "—"}`] });
    if (synth.chronology?.text) s.push({ title: "Chronology", body: [synth.chronology.text] });
    if (synth.classification) s.push({ title: "Complication classification", body: [`Outcome: ${synth.classification.outcomeCategory}`, `Severity: ${synth.classification.severity}`, `Preventability: ${synth.classification.preventability}`] });
    s.push({ title: "Imaging", kind: "imaging", body: [(synth.imagingIds || []).join(", ") || "—"] });
    if (synth.rootCause?.text) s.push({ title: "Root cause", body: [synth.rootCause.text] });
    if (synth.contributingFactors?.length) s.push({ title: "Contributing factors", body: synth.contributingFactors.map((x) => "· " + x.text) });
    if (synth.protectiveFactors?.length) s.push({ title: "Protective factors", body: synth.protectiveFactors.map((x) => "· " + x.text) });
    if (synth.teachingPoints?.length) s.push({ title: "Teaching points", body: synth.teachingPoints.map((x) => "· " + x.text) });
    if (synth.changeInPractice?.text) s.push({ title: "Change in practice", body: [synth.changeInPractice.text] });
    if (synth.actionItems?.length) s.push({ title: "Action items", body: synth.actionItems.map((x) => "· " + x.text) });
    s.push({ title: "Provenance appendix", body: Object.entries(synth.supportingEvidence || {}).flatMap(([k, ids]) => Array.isArray(ids) && ids.length ? [`${k}: ${ids.join(", ")}`] : []) });
    return s;
  }, [c, activeMM, synth, publishedRec]);

  const total = slides.length;
  const cur = slides[slide];
  const next = () => setSlide((s) => Math.min(total - 1, s + 1));
  const prev = () => setSlide((s) => Math.max(0, s - 1));

  useEffect(() => {
    const on = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);

  return (
    <>
      <div className="nn-source-scrim" onClick={onClose} />
      <div
        className="nn-source-panel"
        role="dialog"
        aria-label="M&M preview"
        data-testid="mm-preview-modal"
        style={{ width: "min(920px, calc(100% - 48px))" }}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
          <div>
            <div className="nn-eyebrow">M&M presentation · Demo Preview</div>
            <div className="mt-0.5 text-[14px] font-semibold text-white">{activeMM.mmId}</div>
          </div>
          <button onClick={onClose} className="nn-icon-btn" aria-label="Close">
            <X className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>
        <div className="p-5">
          <div
            className="mx-auto"
            style={{
              aspectRatio: "16 / 9",
              background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02)), rgba(20,28,46,0.9)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              padding: "36px 44px",
              overflow: "hidden",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
            data-testid={`mm-preview-slide-${slide}`}
          >
            <div className="nn-eyebrow">Slide {slide + 1} / {total}</div>
            <h2 className="mt-2 text-[22px] font-semibold tracking-tight text-white">{cur.title}</h2>
            {cur.subtitle && <div className="mt-1 text-[13.5px] text-[hsl(var(--nn-ink-muted))]">{cur.subtitle}</div>}
            {cur.meta && <div className="mt-1 font-mono text-[11px] text-[hsl(var(--nn-ink-faint))]">{cur.meta}</div>}
            {cur.kind === "imaging" && (
              <div className="mt-4 grid place-items-center rounded-xl border border-white/[0.08] bg-black/40" style={{ height: 220 }}>
                <div className="rounded-full border border-white/[0.08] bg-black/25 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">
                  Preview placeholder · imaging {cur.body?.[0]}
                </div>
              </div>
            )}
            {cur.body && cur.kind !== "title" && cur.kind !== "imaging" && (
              <ul className="mt-4 space-y-1.5 text-[13.5px] leading-[20px] text-[hsl(var(--nn-ink))]">
                {cur.body.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button type="button" onClick={prev} className="nn-btn" disabled={slide === 0} data-testid="mm-preview-prev">
              Previous
            </button>
            <span className="font-mono text-[11px] text-[hsl(var(--nn-ink-muted))]">Deterministic demo preview · no .pptx generated</span>
            <button type="button" onClick={next} className="nn-btn" disabled={slide === total - 1} data-testid="mm-preview-next">
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
